import { Block, CheckCircle, Event, VolumeOff, WarningAmber } from '@mui/icons-material';
import {
    Card,
    CardContent,
    Chip,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { OnlyFansAllCounters } from '../../../../../../hooks/useMetricsDailyStats';
import { DashboardProps } from '../SextforceMetricsAccountValueStats';
import ExportButtons from './ExportButtons';

// Format numbers with commas
const formatNumber = (num: number): string => {
    return num.toLocaleString();
};

// Get subscriber status data for pie chart
const getSubscriberStatusData = (
    theme: Theme,
    counters?: OnlyFansAllCounters,
): {
    name: string;
    value: number;
    color: string;
}[] => {
    if (!counters) return [];

    const { subscribers } = counters;

    return [
        { name: 'Active', value: subscribers.active, color: theme.palette.success.main },
        { name: 'Expired', value: subscribers.expired, color: theme.palette.warning.main },
        { name: 'Muted', value: subscribers.muted, color: theme.palette.info.main },
        { name: 'Restricted', value: subscribers.restricted, color: theme.palette.error.main },
        { name: 'Blocked', value: subscribers.blocked, color: theme.palette.grey[900] },
    ];
};

const SubscribersStatusCard: React.FC<DashboardProps> = ({ subscriber, data, isLoading }) => {
    const theme = useTheme();

    if (isLoading) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="rectangular" width="100%" height={300} />
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1}>
                        <Typography variant="h6">Subscriber Status Breakdown</Typography>
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        No data available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const latestData = data[data.length - 1];
    const counters = latestData.counters;

    if (!counters) return null;

    const subscriberData = getSubscriberStatusData(theme, counters);

    return (
        <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1}>
                    <Typography variant="h6">Subscriber Status Breakdown</Typography>
                    <ExportButtons
                        data={subscriberData}
                        componentType="subscriberStatus"
                        filename={`${subscriber.username}_Subscriber_Status`}
                    />
                </Stack>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={subscriberData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                    nameKey="name"
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    fontSize={11}
                                    isAnimationActive={false}
                                >
                                    {subscriberData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={value => [formatNumber(value as number), undefined]} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <List>
                            {subscriberData.map((item, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        {item.name === 'Active' ? (
                                            <CheckCircle sx={{ color: item.color }} />
                                        ) : item.name === 'Expired' ? (
                                            <Event sx={{ color: item.color }} />
                                        ) : item.name === 'Muted' ? (
                                            <VolumeOff sx={{ color: item.color }} />
                                        ) : item.name === 'Restricted' ? (
                                            <WarningAmber sx={{ color: item.color }} />
                                        ) : (
                                            <Block sx={{ color: item.color }} />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary={item.name} secondary={formatNumber(item.value)} />
                                    <Chip
                                        label={`${((item.value / counters.subscribers.all) * 100).toFixed(1)}%`}
                                        size="small"
                                        sx={{ backgroundColor: item.color, color: 'white' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SubscribersStatusCard;
